import PropTypes from 'prop-types';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { he } from 'react-day-picker/locale';
import { useTranslation } from 'react-i18next';
import { getTimeRange, TimeGrid } from '@mantine/dates';
import { Button, LoadingOverlay, Select } from '@mantine/core';
import { getMutationResult } from '@/helpers/query';
import {
  IconCalendar,
  IconClock,
  IconClockHour5,
  IconVideo,
} from '@tabler/icons-react';
import classNames from 'react-day-picker/style.module.css';
import {
  formatDateToString,
  getDayName,
  getLastDayMonthAhead,
  getStartAndEndTime,
  getUnavailableDates,
} from '@/helpers/date';
import { useMeetingCreateMutation, useMeetingSlotsMutation } from '@/services/api';
import { findAvailableSlotId, getDaySlots, getAvailableTimesFromSlots } from '@/helpers/meeting';
import Logo from '@/assets/images/logo-clean.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './meeting.module.css';

const Meeting = (props) => {
  const {
    months = 3, // number of (including current month) months in calendar
    counselors = [],
    onSubmit = () => { },
    offerId = null,
  } = props;

  const { t } = useTranslation();
  const [visibleOverlay, setVisibleOverlay] = useState(false);
  const [apiMeetingSlots] = useMeetingSlotsMutation();
  const [apiMeetingCreate] = useMeetingCreateMutation();
  const [date, setDate] = useState(); // selected date as date object
  const [time, setTime] = useState(); // selected time as HH:MM:SS
  const [counselor, setCounselor] = useState(null); // selected counselor id
  const [loading, setLoading] = useState(false); // submit button loading state
  const [disableTime, setDisabledTime] = useState([]); // disable specific time options
  const [disabledTimeGrid, setDisabledTimeGrid] = useState(true); // disable time grid component
  const [disabledButton, setDisabledButton] = useState(true); // disable submit button
  const [interval, setInterval] = useState('00:45'); // meeting interval HH:MM
  const [excludedDays, setExcludedDays] = useState(null); // exclude days from calendar, slots
  const [slots, setSlots] = useState(null); // counselor slots, api response
  const [availableTimes, setAvailableTimes] = useState([]); // available times for selected date
  const startDay = new Date(); // calendar start day
  const endDay = getLastDayMonthAhead(months); // calendar end day

  const startTime = '01:00'; // time grid start time (used for getDaySlots)
  const endTime = '23:00'; // time grid end time (used for getDaySlots)

  const cleanForm = () => {
    setDate(null);
    setTime(null);
    setDisabledTime(true);
    setDisabledTimeGrid(true);
    setDisabledButton(true);
    setAvailableTimes([]);
  };

  const counselorChange = async (id) => {
    if (!id) return;

    try {
      setVisibleOverlay(true);
      const getSlotsData = { id, startDay, endDay };

      // api call - get counselor meetings days and slots
      const { data, isError } = await getMutationResult(getSlotsData, apiMeetingSlots);

      // TODO: handle error
      if (isError) {
        // eslint-disable-next-line no-console
        console.error('error getting counselor slots');
        return;
      }

      if (data) {
        const days = data.days.map((day) => new Date(day.date)); // get all days
        const unavailableDates = getUnavailableDates(days, startDay, endDay);

        cleanForm();
        setSlots(data.days);
        setInterval(data.interval);
        setCounselor(id);
        setExcludedDays(unavailableDates);
      }
    } catch {
      // empty
    } finally {
      setVisibleOverlay(false);
    }
  };

  const handleDateSelect = (d) => {
    if (!d) return; // Prevent unselecting (clicking same date again)

    // Get available times from slots for this date
    const times = getAvailableTimesFromSlots(slots, d);
    setAvailableTimes(times);

    // TODO: remove disable time options
    const options = getDaySlots(slots, d, startTime, endTime, interval);

    setDisabledTime(options.unavailable);
    setDisabledTimeGrid(false);
    setDate(d);
  };

  const handleButtonSubmit = async () => {
    const { startDate, endDate } = getStartAndEndTime(date, interval, time);
    const id = findAvailableSlotId(slots, startDate, endDate);
    const dataObject = {
      slotId: id,
      startTime: startDate,
      endTime: endDate,
      counselorId: counselor,
      date,
      offerId,
    };

    try {
      setLoading(true);

      // api call - create meeting
      const { data, isError } = await getMutationResult(dataObject, apiMeetingCreate);

      // TODO: handle error
      if (isError) {
        // eslint-disable-next-line no-console
        console.error('error getting counselor slots');
        return;
      }

      if (data) {
        const { isNew, eventLink, success } = data;
        if (success) {
          onSubmit({
            ...dataObject,
            isNew,
            eventLink,
            success,
          });
        } else {
          // TODO: handle success false
          // eslint-disable-next-line no-console
          console.error({ data });
        }
      }
    } catch {
      // TODO: handle catch
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (hour) => {
    setDisabledButton(false);
    setTime(hour);
  };

  return (
    <div className={classes.meeting}>
      <LoadingOverlay visible={visibleOverlay} />
      <div className={classes.info}>
        <div className={classes.avatar}><Logo /></div>
        <div className={classes.name}>{t('components.meeting.info.name')}</div>
        <div className={classes.title}>{t('components.meeting.info.title')}</div>
        <div className={classes.select}>
          <Select
            placeholder={t('components.meeting.counselor.placeholder')}
            data={counselors}
            onChange={counselorChange}
            value={counselor}
          />
        </div>

        <ul className={classes.list}>
          <li>
            <div className={classes.icon}><IconClock /></div>
            <div className={classes.text}>{t('components.meeting.info.time', { minutes: interval })}</div>
          </li>
          <li>
            <div className={classes.icon}><IconVideo /></div>
            <div className={classes.text}>{t('components.meeting.info.video')}</div>
          </li>
          <li>
            <div className={classes.icon}><IconCalendar /></div>
            <div className={classes.text}>{
              (!date)
                ? t('components.meeting.info.date.empty')
                : t('components.meeting.info.date', { date: formatDateToString(date) })
            }</div>
          </li>
          <li>
            <div className={classes.icon}><IconClockHour5 /></div>
            <div className={classes.text}>{
              (!time)
                ? t('components.meeting.info.hour.empty')
                : t('components.meeting.info.hour', { time })
            }</div>
          </li>
        </ul>
      </div>

      {counselor && (<>
        <div className={classes.date}>
          <DayPicker
            animate
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            locale={he}
            startMonth={startDay}
            endMonth={endDay}
            disabled={[
              ...excludedDays,
              { before: startDay },
            ]}
            classNames={{
              ...classNames,
              button_previous: classes.prev,
              button_next: classes.next,
            }}
          />
        </div>
        <div className={classes.time}>
          <div className={classes.head}>
            <span className={classes.day}>{formatDateToString(date)}</span>
            <span className={classes.dayname}>{getDayName(date)}</span>
          </div>
          {date && (
            <TimeGrid
              data={
                availableTimes.length > 0
                  ? availableTimes
                  : getTimeRange({ startTime, endTime, interval })
              }
              value={time}
              onChange={handleTimeChange}
              allowDeselect={false}
              disabled={disabledTimeGrid}
              disableTime={disableTime}
            />
          )}
          <div className={classes.action}>
            <Button
              variant="light"
              onClick={handleButtonSubmit}
              loading={loading}
              disabled={disabledButton}
            >
              {t('components.meeting.time.action')}
            </Button>
          </div>
        </div>
      </>)}
    </div>
  );
};

Meeting.propTypes = {
  months: PropTypes.number,
  counselors: PropTypes.array,
  onSubmit: PropTypes.func,
  offerId: PropTypes.string,
};

export default Meeting;
